import type { StatusDotAnimation, StatusDotColor, StatusDotVariant } from "@/components/flexkit/status-dot/StatusDot";
import type { PlainAgentExecution } from "@/queries/agent-queries";
import type { PlainEnvironment } from "@/queries/environment-queries";
import { type EffectiveState, effectiveState } from "@/routes/environments/phase";
import { agentExecutionState, AgentExecutionState } from "@/utils/agent-executions";
import { gitStatusChangesCount } from "@/utils/git-status";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";

export type ConversationDotProps = {
    color: StatusDotColor;
    variant: StatusDotVariant;
    animation: StatusDotAnimation;
    tooltip: string;
};

// ── Resolved conversation state ─────────────────────────────────────────

type AgentConversationState = {
    kind: "working" | "waiting" | "failed" | "stopping" | "starting" | "review" | "running" | "idle" | "stopped";
};

function resolveAgentConversationState(
    env: PlainEnvironment,
    agentExecution: PlainAgentExecution,
): AgentConversationState {
    const state = agentExecutionState(agentExecution);
    const envState = effectiveState(env);
    const envRunning = envState.state === EnvironmentPhase.RUNNING;
    const hasChanges = (gitStatusChangesCount(env.status?.content?.git) ?? 0) > 0;

    // Active agent states take priority regardless of environment phase
    switch (state) {
        case AgentExecutionState.WORKING:
        case AgentExecutionState.PENDING:
            return { kind: "working" };
        case AgentExecutionState.WAITING_FOR_INPUT:
            return { kind: "waiting" };
        case AgentExecutionState.FAILED:
            return { kind: "failed" };
        case AgentExecutionState.STOPPING:
            return { kind: "stopping" };
    }

    // Environment is starting/resuming — show heartbeat regardless of agent state
    const envStarting =
        envState.state === EnvironmentPhase.STARTING ||
        envState.state === EnvironmentPhase.CREATING ||
        envState.state === EnvironmentPhase.UPDATING;
    if (envStarting) {
        return { kind: "starting" };
    }

    // Terminal agent states: green only when the environment is online.
    // A stopped environment always shows gray, even with reviewable changes.
    if (!envRunning) {
        return state === AgentExecutionState.STOPPED_BY_USER ? { kind: "stopped" } : { kind: "idle" };
    }

    switch (state) {
        case AgentExecutionState.READY_FOR_REVIEW:
            return hasChanges ? { kind: "review" } : { kind: "running" };
        case AgentExecutionState.STOPPED_BY_USER:
            return { kind: "running" };
        default:
            return { kind: "running" };
    }
}

const AGENT_DOT: Record<AgentConversationState["kind"], ConversationDotProps> = {
    working: { color: "brand", variant: "solid", animation: "spin", tooltip: "Working" },
    waiting: { color: "orange", variant: "solid", animation: "ping", tooltip: "Needs attention" },
    failed: { color: "red", variant: "solid", animation: "none", tooltip: "Failed" },
    stopping: { color: "gray", variant: "solid", animation: "fade", tooltip: "Stopping" },
    starting: { color: "brand", variant: "solid", animation: "heartbeat", tooltip: "Starting..." },
    review: { color: "green", variant: "solid", animation: "pulse", tooltip: "Ready for review" },
    running: { color: "green", variant: "solid", animation: "none", tooltip: "Running" },
    idle: { color: "gray", variant: "small", animation: "none", tooltip: "Idle" },
    stopped: { color: "gray", variant: "small", animation: "none", tooltip: "Stopped" },
};

// ── Environment-only state ──────────────────────────────────────────────

function envDotFromState(state: EffectiveState): ConversationDotProps {
    switch (state.state) {
        case EnvironmentPhase.RUNNING:
            return { color: "green", variant: "solid", animation: "none", tooltip: "Running" };
        case EnvironmentPhase.UPDATING:
        case EnvironmentPhase.STARTING:
        case EnvironmentPhase.CREATING:
            return { color: "brand", variant: "solid", animation: "heartbeat", tooltip: "Starting..." };
        case EnvironmentPhase.STOPPING:
        case EnvironmentPhase.DELETING:
            return { color: "gray", variant: "solid", animation: "fade", tooltip: "Stopping" };
        case EnvironmentPhase.STOPPED:
            return state.failures
                ? { color: "red", variant: "solid", animation: "none", tooltip: "Failed to start" }
                : { color: "gray", variant: "small", animation: "none", tooltip: "Stopped" };
        default:
            return { color: "gray", variant: "small", animation: "none", tooltip: "Offline" };
    }
}

function envLabelFromState(state: EffectiveState): string {
    switch (state.state) {
        case EnvironmentPhase.RUNNING:
            return "Ready";
        case EnvironmentPhase.STARTING:
        case EnvironmentPhase.CREATING:
            return "Starting...";
        case EnvironmentPhase.UPDATING:
            return "Updating...";
        case EnvironmentPhase.STOPPING:
        case EnvironmentPhase.DELETING:
            return "Stopping...";
        case EnvironmentPhase.STOPPED:
            return state.failures ? "Failed to start" : "Stopped";
        default:
            return "Offline";
    }
}

// ── Public API ──────────────────────────────────────────────────────────

/**
 * Derives StatusDot props from combined environment + agent execution state.
 */
export function getConversationDotProps(
    env: PlainEnvironment,
    agentExecution?: PlainAgentExecution,
): ConversationDotProps {
    if (agentExecution) {
        const resolved = resolveAgentConversationState(env, agentExecution);
        return AGENT_DOT[resolved.kind];
    }
    return envDotFromState(effectiveState(env));
}

/**
 * Returns a human-readable phase label for environment-only items.
 */
export function getEnvironmentLabel(env: PlainEnvironment): string {
    return envLabelFromState(effectiveState(env));
}
